import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { UseGetStoreData } from "../getStoreData/UseGetStoreData";
import { useNavigate } from "react-router-dom";
import { ShowAlert } from "../../utils/showAlert";
import { errorFormate } from "../../utils/helper";
import { ordersByTicketId, refreshPaymentStatusByTicketId, sendAgainPaymentLink } from "../../pages/actions/actions";
// import { useSseTrigger } from "../UseSseTrigger/UseSseTrigger";

export const usePaymentStatus = () => {

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [paymentBtnStatus_Ambulance, setPaymentBtnStatus_Ambulance] = useState<any>({
        isCreated: false,
        isFailed: false,
        isPaid: false,
        isExpired: false,
        isCanceled: false,
        data: null,
    });
    const [paymentBtnStatus_Doctor, setPaymentBtnStatus_Doctor] = useState<any>({
        isCreated: false,
        isFailed: false,
        isPaid: false,
        isExpired: false,
        isCanceled: false,
        data: null
    });
    const { getTicketData } = UseGetStoreData();
    const fetchOrders = async (ticketId: string) => {
        try {
            const res = await ordersByTicketId(ticketId)
            return res;
        } catch (e: any) {
            if (e.response.statusCode === 404) {
                return
            } else {
                ShowAlert("warning", errorFormate({ e, service: "getPaymentOrders" }));
            }
        };
    }
    const jumpWithNotification = () => {
        ShowAlert("warning", "Some Technical Issue In retrieving Data");
        navigate("/")
    }
    const shouldFetch = getTicketData?._id ? true : jumpWithNotification();
    const { data: getOrderList } = useQuery({
        queryKey: ["getOrdersByTicketId", getTicketData?._id],
        queryFn: shouldFetch ? () => fetchOrders(getTicketData?._id) : skipToken,

    });
    const checkOrderStatus = (payment: any) => {
        const hasDisableBtn = payment.payment_status !== "paid" && payment.payment_status !== "expired" && payment.payment_status !== "cancelled" && payment.payment_status !== "partially_paid" && payment.payment_status !== "failed";
        const hasPaid = payment.payment_status === "paid"
        const hasExpired = payment.payment_status === "expired"
        const hasCancelled = payment.payment_status === "cancelled"
        const hasFailed = payment.payment_status === "failed"
        return { hasDisableBtn, hasPaid, hasExpired, hasCancelled, payment, hasFailed }
    }
    const sendPaymentLink = async (component: string) => {
        const findComponent = getOrderList.find((order: any) => order.notes.order_by === component);
        try {
            await sendAgainPaymentLink(findComponent.provider_link_id)
        } catch (e: any) {
            ShowAlert("warning", e?.response?.data?.message || "Some Technical Issue in Sending PaymentLink")
            console.log('e:', e);
        }
    }
    // console.log('getOrderList:--outside', getOrderList);
    
    // Helper: find the most relevant order when multiple exist for same type
    // Priority: paid > failed > expired > cancelled > latest created
    const findRelevantOrder = (orders: any[], orderByType: string) => {
        const filtered = orders?.filter((o: any) => 
            o?.notes?.order_by?.toLowerCase() === orderByType
        ) || [];
        
        if (!filtered.length) return null;
        
        // If only one order, return it (preserves original single-order behavior)
        if (filtered.length === 1) return filtered[0];
        
        // Priority order: paid status should always be shown if any order is paid
        const paid = filtered.find((o: any) => o.payment_status === 'paid');
        if (paid) return paid;
        
        const failed = filtered.find((o: any) => o.payment_status === 'failed');
        if (failed) return failed;
        
        const expired = filtered.find((o: any) => o.payment_status === 'expired');
        if (expired) return expired;
        
        const cancelled = filtered.find((o: any) => o.payment_status === 'cancelled');
        if (cancelled) return cancelled;
        
        // Default: return the most recent (last in array) for "created" status
        return filtered[filtered.length - 1];
    };

    useEffect(() => {
        // alert("check")
        // console.log('getOrderList:', getOrderList);
        if (!getOrderList?.length) {
            setPaymentBtnStatus_Ambulance({
                isCreated: false,
                isFailed: false,
                isPaid: false,
                isExpired: false,
                isCanceled: false,
                data: null,
            })
            setPaymentBtnStatus_Doctor({

                isCreated: false,
                isFailed: false,
                isPaid: false,
                isExpired: false,
                isCanceled: false,
                data: null,
            })
            return;
        }

        // Find the most relevant order for each type (handles multiple payment links)
        const ambulanceOrder = findRelevantOrder(getOrderList, 'ambulance');
        if (ambulanceOrder) {
            const res = checkOrderStatus(ambulanceOrder);
            setPaymentBtnStatus_Ambulance({
                isCreated: res.hasDisableBtn,
                isPaid: res.hasPaid,
                isExpired: res.hasExpired,
                isCanceled: res.hasCancelled,
                data: res.payment,
                isFailed: res.hasFailed
            });
        }

        const doctorOrder = findRelevantOrder(getOrderList, 'doctor-consultation');
        if (doctorOrder) {
            const res = checkOrderStatus(doctorOrder);
            setPaymentBtnStatus_Doctor({
                isCreated: res.hasDisableBtn,
                isPaid: res.hasPaid,
                isExpired: res.hasExpired,
                isCanceled: res.hasCancelled,
                data: res.payment,
                isFailed: res.hasFailed
            });
        }
        //eslint-disable-next-line
    }, [getOrderList])


    const refreshPaymentStatus = async () => {
        if (!getTicketData?._id) return;
        setIsRefreshing(true);
        try {
            await refreshPaymentStatusByTicketId(getTicketData._id);
            // Invalidate the query to refetch updated data
            await queryClient.invalidateQueries({
                queryKey: ['getOrdersByTicketId', getTicketData._id],
                exact: true,
                refetchType: 'all',
            });
            ShowAlert("success", "Payment status refreshed successfully");
        } catch (e: any) {
            ShowAlert("warning", e?.response?.data?.message || "Failed to refresh payment status");
        } finally {
            setIsRefreshing(false);
        }
    }

    return {
        paymentBtnStatus_Ambulance,
        paymentBtnStatus_Doctor,
        sendPaymentLink,
        refreshPaymentStatus,
        isRefreshing
    };
}